/**
 * Returns information about the user's permissions.  Note for org-specific
 * permissions you must be sure to call ClientPermissions in your Java
 * Controller to ensure appropriate information is sent along with the request.
 */

import globals from 'scoreboard/components/globals'

import Backbone from 'backbone'
import _ from 'lodash'
import constants from 'scoreboard/components/constants'
import arrayToHash from 'scoreboard/components/array-to-hash'

const ORG_HEADER_NAME = 'Permissions-Org-Specific'
/**
 * The event name that is triggered when a XHR is deemed successful and the
 * permissions can be updated from its headers.  The event must emit one argument,
 * the XHR object.
 */
const XHR_HEADERS_AVAILABLE = 'xhr-headers-available'

var permissions = {

	/**
	 * Cache of all permission IDs the user has been assigned via one or more
	 * groups. { 1: true, 2: true }
	 */
	general: {},
	/**
	 * cache of permissions that are org specific, keyed by org ID with a format
	 * like
	 *
	 * {
	 *     1: { 2: true, 3: true }
	 * }
	 *
	 * where 1 is the org ID and 2 and 3 are permission IDs.
	 */
	orgSpecific: {},
	// whether the user is a local admin of at least one group
	_localAdmin: false,
	_readOnly: true,
	_strictViewer: false,
	_importMetricValues: false,
	_activeViewer: false,

	// private method to look for a specific permission.  this should only be
	// used internally.  callers should use a more descriptive method like
	// permissions.relatedItems()
	_hasPermission: function(permissionId, orgId) {
		orgId = orgId || globals.orgId

		// !! ensures the return value is true or false
		return !!(this.orgSpecific[orgId] && this.orgSpecific[orgId][permissionId])
	},

	// ORG SPECIFIC PERMISSIONS

	// ability to add and delete related items
	relatedItems: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYRELATEDITEMS, orgId)
	},

	documents: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYMANAGEDDOCUMENTS, orgId)
	},

	// ability to modify org and scorecard structure
	structure: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_STRUCTUREMODIFY, orgId)
	},

	// ability to edit and delete notes
	notes: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_NOTESMODIFY, orgId)
	},

	modifyInitiatives: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_EDITTASKS, orgId)
	},

	updateInitiativeStatus: function (orgId) {
		return this._hasPermission(constants.SEC_CODE_UPDATE_TASK_STATUS, orgId)
	},

	archiveInitiatives: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_ARCHIVETASKS, orgId)
	},

	// ability to modify and delete reports
	reports: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYREPORTS, orgId)
	},

	// ability to modify and delete charts
	// Note: this is "modify charts and reports" so it is the same underlying
	// permission as permissions.reports
	charts: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYREPORTS, orgId)
	},

	// ability to modify and delete dashboards
	dashboards: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYDASHBOARDS, orgId)
	},

	// ability to modify and delete strategyMaps
	strategyMaps: function(orgId) {
		return this.dashboards(orgId)
	},

	// ability to modify owners and updaters
	ownersUpdaters: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_OWNERUPDATERMODIFY, orgId)
	},

	// ability to modify and delete briefings
	briefings: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYBRIEFINGBOOKS, orgId)
	},

	permissions: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_PERMISSIONSMODIFY, orgId)
	},

	// ability to modify the chart on the scorecard overview
	scorecardOverview: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYSCORECARDOVERVIEW, orgId)
	},

	// ability to view all datasets and access the edit tab for datasets
	modifyAllDatasets: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYALLDATASETS, orgId)
	},

	/**
	 * permission to create and administer the datasets with standard permissions,
	 * not the datasets with advanced permissions. The datasets with advanced permissions
	 * that are explicitly shared to you get the specialized permissions from the
	 * share where you may or may not be an administrator.
	 */
	modifyStandardPermissionDatasets: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYSTANDARDPERMISSIONDATASETS, orgId)
	},

	modifyDatasetRecords: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYDATASETRECORDS, orgId)
	},

	viewDatasetRecords: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_VIEWDATASETRECORDS, orgId)
	},

	viewDatasetRecordsTab: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_VIEWDATASETRECORDSTAB, orgId)
	},

	modifyDatasetViews: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYDATASETVIEWS, orgId)
	},

	/** ability to modify forms */
	forms: function(orgId) {
		return this._hasPermission(constants.SEC_CODE_MODIFYFORMS, orgId)
	},

	// GENERAL PERMISSIONS

	modifyProjects: function() {
		return !!this.general[constants.SEC_CODE_MODIFYPROJECTS]
	},

	/**
	 * Corresponds to "Modify bookmarks and personal settings" in View section
	 */
	bookmarks: function() {
		return !!this.general[constants.SEC_CODE_CHANGEHOMEPAGE]
	},

	anyBriefing: function() {
		return !!this.general[constants.SEC_CODE_MODIFYBRIEFINGBOOKS]
	},

	anyOwnerUpdaters: function() {
		return !!this.general[constants.SEC_CODE_OWNERUPDATERMODIFY]
	},

	anyModifyStandardPermissionDatasets: function() {
		return !!this.general[constants.SEC_CODE_MODIFYSTANDARDPERMISSIONDATASETS]
	},

	anyModifyViewOrgPermissions: function() {
		return !!this.general[constants.SEC_CODE_PERMISSIONSMODIFY]
	},

	anyStructure: function() {
		return !!this.general[constants.SEC_CODE_STRUCTUREMODIFY]
	},

	anyRelatedItems: function() {
		return !!this.general[constants.SEC_CODE_MODIFYRELATEDITEMS]
	},

	/**
	 * True if user has the permission "Update Initiative Status" checked
	 * in a group that the user belongs to.  Note: this does not mean they
	 * can update the status for an initiative for any org, they still must
	 * possess the permission to view the org itself to be able to modify the
	 * status for that initiative in that org.
	 */
	anyUpdateInitiativeStatus: function() {
		return !!this.general[constants.SEC_CODE_UPDATE_TASK_STATUS]
	},

	calendars: function() {
		return !!this.general[constants.SEC_CODE_CALENDARSMODIFY]
	},

	modifyUsers: function() {
		return !!this.general[constants.SEC_CODE_MODIFYUSERS]
	},

	deleteUsers: function() {
		return !!this.general[constants.SEC_CODE_DELETEUSERS]
	},

	impersonate: function() {
		return !!this.general[constants.SEC_CODE_IMPERSONATE]
	},

	appAdmin: function() {
		return !!this.general[constants.SEC_CODE_APPLICATIONCONFIGURE]
	},

	serverAdmin: function() {
		return !!this.general[constants.SEC_CODE_SERVERCONFIGURE]
	},

	allGroups: function() {
		return !!this.general[constants.SEC_CODE_ADMINALLGROUPS]
	},

	personalAccountInfo: function() {
		return !!this.general[constants.SEC_CODE_CHANGE_PASSWORD]
	},

	localAdmin: function() {
		return this._localAdmin
	},

	/**
	 * Returns true if the user has permission to import metric values for at least
	 * one metric
	 */
	importMetricValues: function () {
		return this._importMetricValues
	},

	/**
	 * A read only user is someone whose only group memberships (admin or regular member)
	 * are either in the active viewer or view only groups
	 * @return {boolean}
	 */
	readOnly: function() {
		return this._readOnly
	},

	/**
	 * An active viewer (interactive user) is a user that has been made a member of an
	 * active viewers (interactive users) group.
	 * @return {boolean}
	 */
	activeViewer: function() {
		return this._activeViewer
	},

	/**
	 * ability to view/modify all shared dashboards
	 */
	shareDashboards: function() {
		return !!this.general[constants.SEC_CODE_MODIFYEMBEDDEDDASHBOARDS]
	},

	/**
	 * ability to view/modify all shared forms
	 */
	shareForms: function() {
		return !!this.general[constants.SEC_CODE_MODIFYEMBEDDEDFORMS]
	},

	scheduledExports: function() {
		return !!this.general[constants.SEC_CODE_MODIFYSCHEDULEDEXPORTS]
	},

	readOnlySqlConsole : function() {
		return !!this.general[constants.SEC_CODE_SQL_CONSOLE_READONLY]
	},

	modifyImportConnections: function() {
		return !!this.general[constants.SEC_CODE_MODIFYIMPORTCONNECTIONS]
	},

	modifyImports: function() {
		return !!this.general[constants.SEC_CODE_MODIFYALLIMPORTS]
	},

	updateAllMetrics: function() {
		return !!this.general[constants.SEC_CODE_UPDATEALLMETRICS]
	},

	updateAllThresholds: function() {
		return !!this.general[constants.SEC_CODE_UPDATEALLTHRESHOLDS]
	},

	// permission to promote an organization to the root organization.  The
	// user must have modify structure permission and view all org permission
	// to have this permission
	promote: function() {
		return !!this.general[constants.SEC_CODE_PROMOTE_TO_ROOT]
	},

	// permission to view the admin section
	viewAdmin: function() {
		return !!(this.serverAdmin() ||
			this.appAdmin() ||
			this.anyModifyViewOrgPermissions() ||
			this.anyOwnerUpdaters() ||
			this.calendars() ||
			this.deleteUsers() ||
			this.modifyUsers() ||
			this.allGroups() ||
			this.shareDashboards() ||
			this.localAdmin()) ||
			this.modifyImportConnections() ||
			this.modifyImports()
	},

	isStrictViewer: function() {
		return this._strictViewer
	},

	update: function(xhr) {
		var general = xhr.getResponseHeader('Permissions-General')
		if (!_.isNil(general)) { // Empty string means no permissions
			permissions.general = toObject(general)
		}

		var localAdmin = xhr.getResponseHeader('Permissions-Local-Admin')
		if (localAdmin) {
			permissions._localAdmin = localAdmin === 'true'
		}

		let importMetricValues = xhr.getResponseHeader('Permissions-Import-Metric-Values')
		if (importMetricValues) {
			permissions._importMetricValues = importMetricValues === 'true'
		}

		var orgSpecific = xhr.getResponseHeader(ORG_HEADER_NAME)
		if (!_.isEmpty(orgSpecific)) {
			_.each(orgSpecific.split(';'), orgPermissions => {
				// ids are empty if the user only has permission to view
				// the org and not modify anything
				const [orgId, permissionIds] = orgPermissions.split('=')
				permissions.orgSpecific[orgId] = toObject(permissionIds)
			})
		}

		var readOnly = xhr.getResponseHeader('Permissions-Read-Only')
		if (readOnly) {
			permissions._readOnly = readOnly !== 'false'
		}

		var activeViewer = xhr.getResponseHeader('Permissions-Active-Viewer')
		if (activeViewer) {
			permissions._activeViewer = activeViewer !== 'false'
		}


		/** Not all responses will contain this header, so if it is not present, we cannot
		 *  make assumptions about its value, and we must skip setting it
		 */
		var strictViewer = xhr.getResponseHeader('Permissions-Strict-Viewer')
		if (strictViewer) {
			permissions._strictViewer = 'true' === strictViewer
		}
	},

	isEditableIfReplicated (model) {
		return _.result(model, 'attributes.syncId', null) == null || this.serverAdmin()
	}

}

/**
 * Converts a string like "1,2" into an object like { 1: true, 2: true }
 */
function toObject(str) {
	if (!str) {
		return {}
	}
	return arrayToHash(str.split(','))
}

Backbone.on(XHR_HEADERS_AVAILABLE, xhr => permissions.update(xhr))

_.extend(permissions, {
	toObject,
	ORG_HEADER_NAME,
	XHR_HEADERS_AVAILABLE
})
export default permissions
export { toObject }
export { ORG_HEADER_NAME }
export { XHR_HEADERS_AVAILABLE }

permissions.__filename__ = 'components/permissions.js'
